import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { NumberFormatValues } from 'react-number-format';
import { Icon, IconEnum } from '@investoro/core';
import { useUser } from 'store/User/Context';
import styled from 'styled-components';
import { AnalyticPanelState } from 'utils/types/AnalyticPanelModule';
import { CalculatorInputType, CalculatorSettings } from 'utils/types/Calculator';
import { CalculatorModuleState } from 'utils/types/CalculatorModule';

import { StyledNumeric } from './StyledInputs';

const TableWrapper = styled.div`
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
`;

const Table = styled.table<{ hasThirdColumn?: boolean }>`
    width: 100%;
    min-width: 600px;
    -webkit-overflow-scrolling: touch;
    border-collapse: collapse;
    background-color: white;
    padding: ${(props) => props.hasThirdColumn ? '20px 60px' : '20px 100px'};

    @media (max-width: 490px) {
        padding: 20px;
    }

`;

const SectionHeaderRow = styled.tr<{ bgColor: string }>`
    font-weight: 600;
    text-align: center;
    color: var(--color-black);
    background-color: var(--color-white);
`;

const TableRow = styled.tr<{ isGray?: boolean }>`
    background-color: ${(props) => props.isGray ? 'var(--color-alt-second)' : 'var(--color-white)'};
`;

const TableCell = styled.td<{ isFirst?: boolean; isLast?: boolean, alignRight?: boolean, highLight?: boolean }>`
    padding: 6px 12px;
    border: none;
    vertical-align: middle;
    background-color: ${({ highLight }) => highLight ? 'var(--color-alt)' : 'transparent'};
    color: ${({ highLight }) => highLight ? '#ffffff' : 'inherit'};
    text-align: ${({ alignRight, alignCenter }) => alignCenter ? 'center' : alignRight ? 'right' : 'left'};

    @media (max-width: 490px) {
        max-width: 50px;
        padding: 6px;
    }
`;

const TableCellContentWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 30px;
    gap: 26px;
    
    td {
        text-align: center;
    }
    
    @media (max-width: 490px) {
        padding: 0 6px;
        gap: 6px;
    }
`;

const SpacerRow = styled.tr`
    height: 30px;
    background-color: var(--color-white);
`;

interface IconWrapperProps {
    darkMode?: boolean;
}

const IconWrapper = styled.div<IconWrapperProps>`
    cursor: pointer;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;

    img {
            filter: ${(props) => props.disabled
                ? 'invert(68%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(84%) contrast(88%)'
                : props.darkMode
                    ? 'brightness(0) invert(1)'
                    : 'none'};
        }
   
    &:hover {
        background: ${(props) => props.disabled ? '' : 'var(--color-alt)'};

        img {
            filter: ${(props) => props.disabled ? 'invert(68%) sepia(0%) saturate(0%) hue-rotate(180deg) brightness(84%) contrast(88%)' : 'invert(100%) sepia(100%) saturate(0%) hue-rotate(79deg) brightness(105%) contrast(102%)'};
        }
    }
`;

export interface CalculatorField {
    title: string;
    fieldName: keyof CalculatorSettings;
    type: CalculatorInputType;
    value: string;
    min?: number;
    max?: number;
    maxLength?: number;
    suffix?: string;
    helperText?: string;
    result?: string;
    readonly?: boolean;
    resultIsEditable?: boolean;
    totalSuffix?: string;
    totalMax?: number;
}

export interface CalculatorSection {
    title: string;
    fields: CalculatorField[];
}

interface CalculatorTableProps {
    sections: CalculatorSection[];
    calculatorState: CalculatorSettings | CalculatorModuleState | AnalyticPanelState;
    additionalValues?: Record<string, string>;
    area?: number;
    onValueChange: (fieldName: keyof CalculatorSettings, value: string) => void;
    onUpdateField: (fieldName: keyof CalculatorSettings, value: string) => Promise<void>;
    onUpdateTotalField?: (fieldName: keyof CalculatorSettings, value: string) => Promise<void>;
    onResetField: (fieldName: keyof CalculatorSettings) => void;
    hasThirdColumn?: boolean;
    initialTotalValues?: Record<string, string>;
}

const CalculatorTable: FC<CalculatorTableProps> = ({
    sections,
    calculatorState,
    onValueChange,
    onUpdateField,
    onUpdateTotalField,
    onResetField,
    hasThirdColumn,
    initialTotalValues
}) => {
    const { user } = useUser();
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
    const [editableFields, setEditableFields] = useState<Record<string, boolean>>({});
    const [editableTotalFields, setEditableTotalFields] = useState<Record<string, boolean>>({});
    const [totalValues, setTotalValues] = useState<Record<string, string>>(initialTotalValues || {});

    useEffect(() => {
        if (initialTotalValues) {
            setTotalValues(initialTotalValues);
        }
    }, [initialTotalValues]);

    const handleFocus = (fieldName: string) => {
        setEditableFields((prev) => ({ ...prev, [fieldName]: true }));
    };

    const handleFocusTotal = (fieldName: string) => {
        setEditableTotalFields((prev) => ({ ...prev, [fieldName]: true }));
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.currentTarget.blur();
        }
    };

    const handleBlur = async (fieldName: keyof CalculatorSettings) => {
        setEditableFields((prev) => ({ ...prev, [fieldName]: false }));

        const value = (calculatorState as unknown as Record<string, unknown>)[fieldName];
        await onUpdateField(fieldName, value != null ? value.toString() : '');
    };

    const handleBlurTotal = async (fieldName: keyof CalculatorSettings) => {
        setEditableTotalFields((prev) => ({ ...prev, [fieldName]: false }));

        if (onUpdateTotalField) {
            const fieldValue = totalValues[fieldName];
            await onUpdateTotalField(fieldName, fieldValue != null ? fieldValue.toString() : '');
        }
    };

    const handleChange = (
        fieldName: keyof CalculatorSettings,
        value: string
    ) => {
        onValueChange(fieldName, value);
    };

    const handleChangeTotalValue = (fieldName: keyof CalculatorSettings, value: string) => {
        setTotalValues((prev) => ({ ...prev, [fieldName]: value }));
    };

    return (
        <TableWrapper>
            <Table>
                {sections.map((section) => {
                    return (
                        <Fragment key={section.title}>
                            <tbody>
                                <SectionHeaderRow >
                                    <TableCell alignCenter colSpan={hasThirdColumn ? 3 : 2}>{section.title}</TableCell>
                                </SectionHeaderRow>

                                <tr>
                                    <TableCell isFirst alignCenter highLight >Nazwa</TableCell>
                                    <TableCell alignCenter highLight >Wartość</TableCell>
                                    {hasThirdColumn && <TableCell highLight isLast alignCenter >Łącznie</TableCell>}
                                </tr>
                                {section.fields.map((field, fieldIndex) => {
                                    const isGray = fieldIndex % 2 === 0;

                                    return (
                                        <TableRow key={field.fieldName} isGray={isGray}>
                                            <TableCell isFirst>{field.title}</TableCell>
                                            <TableCell>
                                                {!field.readonly && <TableCellContentWrapper>
                                                    <StyledNumeric
                                                        value={(calculatorState as unknown as Record<string, unknown>)[field.fieldName] as string}
                                                        onFocus={() => handleFocus(field.fieldName)}
                                                        onBlur={() => handleBlur(field.fieldName)}
                                                        onValueChange={(values: NumberFormatValues) => handleChange(field.fieldName, values.value)}
                                                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e)}
                                                        thousandSeparator=" "
                                                        decimalSeparator=","
                                                        allowDecimal={true}
                                                        decimalScale={2}
                                                        allowNegative={false}
                                                        suffix={` ${field.suffix}`}
                                                        isEdited={editableFields[field.fieldName]}
                                                        ref={(el: HTMLInputElement | null) => inputRefs.current[field.fieldName] = el}
                                                        maxLength={field.maxLength}
                                                        max={field.max}
                                                        min={field.min}
                                                        // @todo sprawdzić czy da się walidować bez isAllowed
                                                        isAllowed={(values: NumberFormatValues) => {
                                                            if (values.value === '') return true;
                                                            if (values.value.length > 1 && values.value.startsWith('0') && !values.value.startsWith('0,')) return false;
                                                            const num = Number(values.value);
                                                            if (field.min !== undefined && num < field.min) return false;
                                                            if (field.max !== undefined && num > field.max) return false;

                                                            return true;
                                                        }}
                                                    />
                                                    <IconWrapper
                                                        darkMode={user.isDarkMode}
                                                        onClick={() => onResetField(field.fieldName)}
                                                    >
                                                        <Icon icon={IconEnum.RELOAD} />
                                                    </IconWrapper>
                                                </TableCellContentWrapper>}
                                            </TableCell>
                                            {hasThirdColumn && (!field.resultIsEditable
                                                ? <TableCellContentWrapper><TableCell isLast>{field.result}</TableCell></TableCellContentWrapper>
                                                : <TableCell>
                                                    <TableCellContentWrapper>
                                                        <StyledNumeric
                                                            value={totalValues[field.fieldName] || ''}
                                                            onFocus={() => handleFocusTotal(field.fieldName)}
                                                            onBlur={() => handleBlurTotal(field.fieldName)}
                                                            onValueChange={(values: NumberFormatValues) => handleChangeTotalValue(field.fieldName, values.value)}
                                                            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e)}
                                                            thousandSeparator=" "
                                                            decimalSeparator=","
                                                            allowDecimal={true}
                                                            decimalScale={2}
                                                            allowNegative={false}
                                                            suffix={` ${field.totalSuffix}`}
                                                            isEdited={editableTotalFields[field.fieldName]}
                                                            ref={(el: HTMLInputElement | null) => inputRefs.current[`${field.fieldName}_total`] = el}
                                                            isAllowed={(values: NumberFormatValues) => {
                                                                if (values.value === '') return true;
                                                                if (values.value.length > 1 && values.value.startsWith('0') && !values.value.startsWith('0,')) return false;
                                                                const num = Number(values.value);
                                                                if (num < 0) return false;
                                                                const maxVal = field.totalMax ?? 999999999;
                                                                if (num > maxVal) return false;

                                                                return true;
                                                            }}
                                                        />
                                                        <IconWrapper
                                                            darkMode={user.isDarkMode}
                                                            onClick={() => onResetField(field.fieldName)}
                                                        >
                                                            <Icon icon={IconEnum.RELOAD} />
                                                        </IconWrapper>
                                                    </TableCellContentWrapper>
                                                </TableCell>)}
                                        </TableRow>
                                    );
                                })}
                            </tbody>
                            <tbody>
                                <SpacerRow>
                                    <td colSpan={hasThirdColumn ? 3 : 2}></td>
                                </SpacerRow>
                            </tbody>
                        </Fragment>
                    );
                })}
            </Table>
        </TableWrapper>

    );
};

export default CalculatorTable;
